import React, { useState, useEffect } from 'react'
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import { SideCard } from './SideCard'
import { FormInput } from '../ui'
import { themeColors } from '../../app/layout/theme'

interface RequirementsSideCardProps {
  value: string | null | undefined
  onSave: (val: string | null) => any
  isPending?: boolean
  canEdit?: boolean
  placeholder?: string
  emptyText?: string
  rows?: number
}

export function RequirementsSideCard({
  value,
  onSave,
  isPending = false,
  canEdit = true,
  placeholder = 'Enter requirements...',
  emptyText = 'No requirements specified.',
  rows = 3,
}: RequirementsSideCardProps) {
  const [editing, setEditing] = useState(false)
  const [tempVal, setTempVal] = useState('')

  useEffect(() => {
    if (!isPending) {
      setEditing(false)
    }
  }, [value, isPending])

  const handleEdit = () => {
    setTempVal(value ?? '')
    setEditing(true)
  }

  const handleSave = async () => {
    const trimmed = tempVal.trim() || null
    await onSave(trimmed)
  }

  const handleCancel = () => {
    setEditing(false)
  }

  const actionButton = !editing && canEdit ? (
    <Tooltip title="Edit requirements">
      <IconButton
        size="small"
        onClick={handleEdit}
        sx={{
          color: '#9ca3af',
          p: 0.25,
          '&:hover': {
            color: themeColors.primary,
            bgcolor: 'rgba(99, 102, 241, 0.08)',
          },
        }}
      >
        <EditIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  ) : undefined

  return (
    <SideCard title="Requirements" action={actionButton}>
      {editing ? (
        <Box>
          <FormInput
            value={tempVal}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempVal(e.target.value)}
            multiline
            rows={rows}
            fullWidth
            autoFocus
            placeholder={placeholder}
            sx={{ '& .MuiInputBase-root': { fontSize: '12.5px' }, mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              disabled={isPending}
              onClick={handleSave}
              sx={{
                borderRadius: '6px',
                fontSize: '11px',
                textTransform: 'none',
                background: themeColors.primary,
                '&:hover': { background: themeColors.primaryDark },
              }}
            >
              {isPending ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
            <Button
              size="small"
              onClick={handleCancel}
              sx={{ borderRadius: '6px', fontSize: '11px', textTransform: 'none', color: '#6b7280', borderColor: '#d1d5db' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : value ? (
        <Typography
          fontSize="12.5px"
          color="text.primary"
          sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
        >
          {value}
        </Typography>
      ) : (
        <Typography
          fontSize="12.5px"
          color="#9ca3af"
        >
          {emptyText}
        </Typography>
      )}
    </SideCard>
  )
}
